import {
  NOTICE_ROOT_ATTRIBUTE,
  resolveNoticeLayout,
  type ResolvedNoticeAppearance,
} from "../shared/appearance";
import {
  CONTROL_ATTRIBUTE,
  NOTICE_ID_ATTRIBUTE,
  STYLE_RADII,
  SWITCH_CONTROL,
} from "../appearance/constants";
import { resolveNoticeLook } from "../appearance/host-settings";
import { part } from "../appearance/parts";
import { scaleFontSizes, textScaleFactor } from "../appearance/text-scale";
import { buildThemeCssVariables } from "../appearance/theme-vars";
import type { Style } from "../appearance/types";
import { DEFAULT_SELECTION_CONTROL, NOTICE_FONT_ROOT_ATTRIBUTE } from "../constants";
import { inlineStyles } from "../styles/inline";
import type { ActiveConfig, SelectionControlType, Settings } from "../api/types";

type InlineStyles = { [K in keyof typeof inlineStyles]: Style };

export type InlineLookInput = Readonly<{
  noticeId: string;
  content: ActiveConfig | null;
  settings?: Partial<Settings>;
  prefersDark: boolean;
  isMobile: boolean;
  fontFamily: string;
}>;

/**
 * What the inline embed draws with. It sits in the host's own card, so it
 * takes the console's text and control colours, the selection control and the
 * text scale, and paints a panel of its own only when the palette sets a
 * background its text colours were chosen for.
 */
export type InlineLook = Readonly<{
  appearance: ResolvedNoticeAppearance;
  styles: InlineStyles;
  responsive: Record<string, Style>;
  headingColor: string;
  textColor: string;
  descriptionColor: string;
  chevronColor: string;
  selectionControl: SelectionControlType;
  drawsSwitches: boolean;
  rootStyle: Style;
  rootVariables: Record<string, string>;
  rootAttributes: Record<string, string>;
}>;

export const buildInlineLook = ({
  noticeId,
  content,
  settings,
  prefersDark,
  isMobile,
  fontFamily,
}: InlineLookInput): InlineLook => {
  const appearance = resolveNoticeLook(content?.appearance, settings, prefersDark);
  const { colors } = appearance;
  const drawsSwitches = resolveNoticeLayout(appearance).switchControl;
  const factor = textScaleFactor(appearance);
  const styles = scaleFontSizes(inlineStyles as InlineStyles, factor);
  const responsive = scaleFontSizes(
    {
      optionTitle: { fontSize: isMobile ? "14px" : "16px" },
      optionDescription: { fontSize: isMobile ? "11px" : "12px" },
      dataElementText: { fontSize: isMobile ? "12px" : "14px" },
    },
    factor
  );
  const textColor = colors.text || "#344054";
  // The brand accent, applied to the embed's native controls. Absent unless
  // the admin set one, so the browser's own default stays as it always was.
  const accent = colors.toggle_on ?? colors.accept_all_bg;

  const rootStyle: Style = {
    ...styles.root,
    ...(colors.background
      ? {
          ...styles.paintedRoot,
          backgroundColor: colors.background,
          borderRadius: appearance.radius || STYLE_RADII[appearance.style].modal,
        }
      : {}),
    // Inherited by what draws no colour of its own (the Yes / No labels).
    ...(colors.text ? { color: colors.text } : {}),
    fontFamily,
    ...(accent ? { accentColor: accent } : {}),
  };

  return {
    appearance,
    styles,
    responsive,
    headingColor: colors.heading || "#101828",
    textColor,
    descriptionColor: colors.muted_text || textColor,
    chevronColor: colors.heading || "#323B4B",
    selectionControl: appearance.selectionControl ?? DEFAULT_SELECTION_CONTROL,
    drawsSwitches,
    rootStyle,
    rootVariables: buildThemeCssVariables(colors),
    // The admin's custom CSS is scoped to the element carrying the notice id;
    // the switch stylesheet to a notice root that asks for switches.
    rootAttributes: {
      [NOTICE_ID_ATTRIBUTE]: noticeId,
      [NOTICE_ROOT_ATTRIBUTE]: "",
      [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
      ...(drawsSwitches ? { [CONTROL_ATTRIBUTE]: SWITCH_CONTROL } : {}),
      ...part("root"),
    },
  };
};
